import { ApolloClient, ApolloLink, CombinedGraphQLErrors, HttpLink } from "@apollo/client";
import { ErrorLink } from "@apollo/client/link/error";
import { InMemoryCache } from "@apollo/client/cache";
import { RetryLink } from "@apollo/client/link/retry";
import { RemoveTypenameFromVariablesLink } from "@apollo/client/link/remove-typename";
import { getLocalStorageLang } from "@domain/context/AppSettingsProvider";
import { getServerlessApi, getServerlessHost } from "@domain/api/backendApi";
import { logger } from "@domain/infrastructure/logger";
import { SetContextLink } from "@apollo/client/link/context";
import { type GraphQLFormattedError } from "graphql";
import { toast } from "@heroui/react";
import { idempotencyLink } from "@domain/graphql/idempotencyLink";
import { requestHashLink } from "@domain/graphql/requestHashLink";
import { retryLinkOptions } from "@domain/graphql/retryPolicy";

const errorLink = new ErrorLink((e) => {
    const langErrors: GraphQLFormattedError[] = [];
    // console.log(CombinedGraphQLErrors.is(e), e);
    if (CombinedGraphQLErrors.is(e.error)) {
        e?.error?.errors?.forEach((e) => {
            if (e.extensions.code === "BINALE_ERROR") {
                langErrors.push(e);
                return;
            }
            if (e.extensions.code === "BINALE_DATEV_TOKEN_CHECK") {
                return;
            }
            logger.error(e.message);
        });
    }

    langErrors.forEach((e) => {
        toast.warning(e.message);
    });
});

// const forwardLink = new ApolloLink((operation, forward) => {
//     return forward(operation).toPromise(data => {
//         if (data?.errors?.length > 0) {
//             const hasClientOffline = data.errors.some(v => v.message.includes("Client is Offline"));
//             if (hasClientOffline) {
//                 throw new Error("Caught GraphQL Operational Error");
//             }
//         }
//         return data;
//     });
// });

const dynamicHeaders = new SetContextLink(async ({ headers }, _s) => {
    const { headers: appHeaders } = await getServerlessApi();
    return {
        headers: {
            ...headers,
            ...appHeaders,
            "x-binale-client-lang": getLocalStorageLang(),
        },
    };
});

export const getApolloClient = () => {
    const uri = getServerlessHost() + "/graphql";

    // const uri = "http://localhost:5001/steuerflow-testing/us-central1/serverless/graphql";
    const link = ApolloLink.from([
        // RetryLink first so that on retry the entire downstream chain re-runs
        // with the same operation context - preserving the Idempotency-Key
        // (and matching X-Request-Hash) across network-error retries so the
        // backend can dedupe correctly.
        new RetryLink(retryLinkOptions),
        dynamicHeaders,
        // Strip __typename from all outgoing variables: Apollo annotates cached
        // objects with __typename, and forms seeded from query results would
        // otherwise send it back into *Input types that reject it. Placed BEFORE
        // requestHashLink so the idempotency hash is computed over the same
        // (stripped) variables that are actually sent. No `except` needed - the
        // only opaque payload (InvoiceGenerateXRechnungXmlInput.json) is a String
        // scalar, so the link never descends into its serialized contents.
        new RemoveTypenameFromVariablesLink(),
        idempotencyLink,
        requestHashLink,
        errorLink,
        // forwardLink,
        new HttpLink({ uri, headers: { "x-binale-client-version": import.meta.env.VITE_VERSION } }),
    ]);
    return new ApolloClient({
        link,
        // No client-side caching: every operation goes to the backend.
        //
        // The app uses none of Apollo's cache features - no update()/refetchQueries/
        // optimisticResponse and no cache.{read,write}{Query,Fragment}/modify/evict
        // anywhere - so the normalized store earned nothing on reads while actively
        // corrupting data on writes: the previous custom `dataIdFromObject` forced a
        // cache key for *every* object, so id-less value objects (CompanyRegister,
        // CompanyTax, CompanyVat) all collapsed onto a single shared key
        // (`Type_undefined_undefined_undefined`) and overwrote each other - e.g. a
        // company's invoiceRegister and invoiceGeneralPartnerInvoiceRegister rendering
        // identical values, and registers/tax bleeding across companies.
        //
        // `no-cache` is the DEFAULT for every operation, so a query that omits an
        // explicit fetchPolicy can never silently fall back to a cache-first read.
        // InMemoryCache is still mandatory for ApolloClient but stays inert.
        cache: new InMemoryCache(),
        defaultOptions: {
            watchQuery: { fetchPolicy: "no-cache" },
            query: { fetchPolicy: "no-cache" },
            mutate: { fetchPolicy: "no-cache" },
        },
    });
};
